import { atom, unstable_createStore } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { NumberPortingFormProps } from "@/components/Checkout/Steps/NumberPorting/NumberPorting";
import { SimVerificationFormProps } from "@/components/Checkout/Steps/SimVerification/SimVerification";
import { BusinessPersonalDetailsFormFields } from "@/components/Portal/UserAccount/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { SimTransferStates } from "@/hooks/useSimTransferStateInfo";
import {
  Basket,
  Product,
  SimCardDetail,
  SubscriberOrderResponse,
} from "@/services/model";

import { TransformedProduct } from "./tariffUtils";
import { WizardEntry } from "./wizardNavigation";

/**
 * Persisted DataContext (migrated from old DataContextProvider's manual localStorage usage)
 * Use createJSONStorage to ensure we use JSON storage and session/prod/local behaviour consistent with your other atoms.
 */
export type DataContextInterface = {
  basket?: Basket;
  personalDetails?: BusinessPersonalDetailsFormFields;
  selectedOption?: TransformedProduct;
  numberPorting?: NumberPortingFormProps;
  checkout?: object;
  simVerification?: SimVerificationFormProps;
  allowedMaxOnboardingStep?: number;
  isEmailVerified?: boolean;
  wizardEntry?: WizardEntry;
};

export const store = unstable_createStore();

export const accessTokenAtom = atomWithStorage<string | null>(
  "accessToken",
  null,
);

export const refreshTokenAtom = atomWithStorage<string | null>(
  "refreshToken",
  null,
);

export const isAnonymousUserAtom = atomWithStorage<boolean>(
  "isAnonymousUser",
  false,
);

export const setAuthAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      accessToken: string | null;
      refreshToken: string | null;
      isAnonymous?: boolean;
      isAgent?: boolean;
    },
  ) => {
    set(accessTokenAtom, payload.accessToken);
    set(refreshTokenAtom, payload.refreshToken);
    set(isAnonymousUserAtom, payload.isAnonymous ?? false);
    set(isAgentAtom, payload.isAgent ?? false);
    // Clear atomically in the same Jotai write-atom batch so no intermediate
    // render sees isAnonymousUser=false while the modal is still true.
    set(showsSessionExpiredModalAtom, undefined);
  },
);

export const clearAuthAtom = atom(null, (_get, set) => {
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);
  set(isAnonymousUserAtom, true);
  set(isAgentAtom, false);
});

export const isAgentAtom = atomWithStorage<boolean>("isAgent", false);

export const verifierAtom = atomWithStorage<string | undefined>(
  "verifier",
  undefined,
);

export const dataContextAtom = atomWithStorage<DataContextInterface>(
  "dataContext",
  {
    allowedMaxOnboardingStep: 1,
    isEmailVerified: false,
  },
  createJSONStorage(() => localStorage),
);

export const destinationAtom = atomWithStorage<string | undefined>(
  "destination",
  undefined,
);

export const tariffCheckoutPaymentPersistenceAtom = atomWithStorage<
  | {
      product?: Product;
      subscriberOrderResponse?: SubscriberOrderResponse;
      simDetail?: SimCardDetail;
    }
  | undefined
>("tariffCheckoutPaymentPersistence", undefined);

export const portalTariffProductIDAtom = atom<number | undefined>(undefined);

export enum AlertTypes {
  "error",
  "info",
  "success",
  "warning",
}

export interface AlertInterface {
  type: AlertTypes;
  title?: string;
  text: string;
}

export const alertListAtom = atom<AlertInterface[]>([]);

export type UserAccountState = {
  editingMail: boolean;
  editingPassword: boolean;
  editingPersonalData: boolean;
  editingPayment: boolean;
  editingSecurity: boolean;
};
export const userAccountStateAtomInitialState: UserAccountState = {
  editingMail: false,
  editingPassword: false,
  editingPersonalData: false,
  editingPayment: false,
  editingSecurity: false,
};
export const userAccountStateAtom = atom<UserAccountState>(
  userAccountStateAtomInitialState,
);

export const isEditingSimDetailsAtom = atom<boolean>(false);

/** True while a SIM-Details inline section (eSIM actions, network reset, …) is open. */
export const isSimDetailsSectionActiveAtom = atom<boolean>(false);

export type DocumentsEditState = {
  billsSelecting: boolean;
  itemizedBillsSelecting: boolean;
};

export const documentsEditStateAtomInitialState: DocumentsEditState = {
  billsSelecting: false,
  itemizedBillsSelecting: false,
};

export const documentsEditStateAtom = atom<DocumentsEditState>(
  documentsEditStateAtomInitialState,
);

export const isListViewAtom = atomWithStorage<boolean | undefined>(
  "listView",
  false,
);

export const basketIsUpdatedAtom = atom<boolean>(false);

export const esimTransferTypeAtom = atom<SimTransferStates>({});

export const showsSessionExpiredModalAtom = atom<boolean | undefined>(
  undefined,
);

export const showsBillingActionBlockedModalAtom = atom<boolean>(false);

export const twoFactorBackupCodesAtom = atom<number[]>([]);

export const phoneNumberAtom = atom<string>("");
